import { useState } from 'react'
import { Card, Table, Button, Form, Row, Col, Spinner, Alert, Modal } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import api from '../../services/api'
import { formatDateTimeEsMX, getCurrentYearMx, getTodayDateOnlyMx } from '../../utils/apiDateFormat'

const formatDate = (value) => formatDateTimeEsMX(value, { dateStyle: 'short', timeStyle: 'short' }, '-')

const FiscalPeriodsList = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [newYear, setNewYear] = useState(getCurrentYearMx())
  const [newMonth, setNewMonth] = useState(parseInt(getTodayDateOnlyMx().split('-')[1], 10))

  const { data: periods, isLoading } = useQuery(
    'fiscal-periods',
    async () => {
      const response = await api.get('/accounting/fiscal-periods/')
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  const createMutation = useMutation(
    async (data) => {
      const response = await api.post('/accounting/fiscal-periods/', data)
      return response.data
    },
    {
      onSuccess: () => {
        toast.success('Período creado correctamente')
        setShowCreate(false)
        queryClient.invalidateQueries('fiscal-periods')
        queryClient.invalidateQueries('fiscal-periods-for-tax-calculation')
      },
      onError: (error) => {
        const msg = error.response?.data?.year?.[0]
          || error.response?.data?.month?.[0]
          || error.response?.data?.non_field_errors?.[0]
          || 'Error al crear período'
        toast.error(msg)
      }
    }
  )

  const closeMutation = useMutation(
    async (id) => api.post(`/accounting/fiscal-periods/${id}/close/`),
    {
      onSuccess: () => {
        toast.success('Período cerrado')
        queryClient.invalidateQueries('fiscal-periods')
        queryClient.invalidateQueries('fiscal-periods-for-tax-calculation')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al cerrar período')
      }
    }
  )

  const reopenMutation = useMutation(
    async (id) => api.post(`/accounting/fiscal-periods/${id}/reopen/`),
    {
      onSuccess: () => {
        toast.success('Período reabierto')
        queryClient.invalidateQueries('fiscal-periods')
        queryClient.invalidateQueries('fiscal-periods-for-tax-calculation')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al reabrir período')
      }
    }
  )

  const handleCreate = (e) => {
    e.preventDefault()
    createMutation.mutate({ year: newYear, month: newMonth })
  }

  const sortedPeriods = periods
    ? [...periods].sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year
        return b.month - a.month
      })
    : []

  const years = Array.from({ length: 8 }, (_, i) => getCurrentYearMx() - 2 + i)
  const months = [
    { value: 1, label: 'Enero' }, { value: 2, label: 'Febrero' }, { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' }, { value: 5, label: 'Mayo' }, { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' }, { value: 11, label: 'Noviembre' }, { value: 12, label: 'Diciembre' }
  ]

  return (
    <div className="fiscal-periods-list">
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Períodos Fiscales</h5>
          <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>
            Crear período
          </Button>
        </Card.Header>
        <Card.Body>
          <p className="text-muted small mb-3">
            Los períodos fiscales definen los meses para reportes contables y cálculo de impuestos.
            Un período cerrado no permite nuevas transacciones ni reversiones.
          </p>
          {isLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : sortedPeriods.length === 0 ? (
            <Alert variant="warning">
              No hay períodos fiscales. Crea al menos uno manualmente o ejecuta en backend:{' '}
              <code>python manage.py seed_fiscal_periods</code>
            </Alert>
          ) : (
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Año</th>
                  <th>Mes</th>
                  <th>Estado</th>
                  <th>Cerrado el</th>
                  <th>Cerrado por</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {sortedPeriods.map((p) => (
                  <tr key={p.id}>
                    <td>{p.year}</td>
                    <td>{months.find((m) => m.value === p.month)?.label || p.month}</td>
                    <td>
                      {p.is_closed ? (
                        <span className="badge bg-secondary">Cerrado</span>
                      ) : (
                        <span className="badge bg-success">Abierto</span>
                      )}
                    </td>
                    <td>{formatDate(p.closed_at)}</td>
                    <td>{p.closed_by_username || '-'}</td>
                    <td>
                      {p.is_closed ? (
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() => reopenMutation.mutate(p.id)}
                          disabled={reopenMutation.isLoading}
                        >
                          Reabrir
                        </Button>
                      ) : (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => closeMutation.mutate(p.id)}
                          disabled={closeMutation.isLoading}
                        >
                          Cerrar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showCreate} onHide={() => setShowCreate(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Crear período fiscal</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreate}>
          <Modal.Body>
            <Row className="g-3">
              <Col>
                <Form.Label>Año</Form.Label>
                <Form.Select
                  value={newYear}
                  onChange={(e) => setNewYear(Number(e.target.value))}
                  required
                >
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col>
                <Form.Label>Mes</Form.Label>
                <Form.Select
                  value={newMonth}
                  onChange={(e) => setNewMonth(Number(e.target.value))}
                  required
                >
                  {months.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={createMutation.isLoading}
            >
              {createMutation.isLoading ? <Spinner animation="border" size="sm" /> : 'Crear'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}

export default FiscalPeriodsList
